import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Paper,
} from "@mui/material";
import { CheckCircle, AccessTime, Close } from "@mui/icons-material";
import api from "../../api/axios";
import socket from "../../socket";
import { useSnackbar } from "../../utils/SnackbarProvider";

const VendorOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const vendor = JSON.parse(localStorage.getItem("user"));
  const { showSnackbar } = useSnackbar();
  // ⛔ Protect vendor route
  useEffect(() => {
    if (!vendor?._id) {
      navigate("/vendor/login", { replace: true });
    }
  }, [vendor, navigate]);

  // 📦 Fetch order
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/vendor/orders/${orderId}`, {
        params: { vendorId: vendor._id },
      });
      setOrder(data);

      /* Join vendor room for real-time updates */
      socket.emit("vendor_join", vendor._id);
    } catch (error) {
      console.error("Failed to load order:", error);
      navigate("/vendor/orders", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [orderId, vendor?._id, navigate]);

  useEffect(() => {
    if (vendor?._id && orderId) {
      fetchOrder();
    }
  }, [fetchOrder]);

  // 📡 Listen for real-time updates
  useEffect(() => {
    socket.emit("join_order", orderId);
    console.log("✓ Vendor joined order room:", orderId);

    // Vendor phase updates
    socket.on("vendor_order_updated", (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev,
          vendorPhase: data.vendorPhase,
          agentPhase: data.agentPhase,
          deliveryPhase: data.deliveryPhase,
          tracking: data.tracking,
        }));
      }
    });

    // Agent accepted delivery
    socket.on("agent_accepted", (data) => {
      if (data.orderId === orderId) {
        console.log("🟠 Agent accepted delivery:", data);
        setOrder((prev) => ({
          ...prev,
          agentPhase: data.agentPhase,
          agent: data.agent,
          tracking: data.tracking,
          status: data.status,
        }));
      }
    });

    // Order status updates from agent actions
    socket.on("order_status_updated", (data) => {
      if (data.orderId === orderId) {
        console.log("📦 Order status updated:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: data.deliveryPhase,
          agentPhase: data.agentPhase,
        }));
      }
    });

    // Agent picked up order
    socket.on("order_picked_up", (data) => {
      if (data.orderId === orderId) {
        console.log("📍 Agent picked up order:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: { ...prev.deliveryPhase, status: "PICKED_UP" },
        }));
      }
    });

    // Agent in transit
    socket.on("order_in_transit", (data) => {
      if (data.orderId === orderId) {
        console.log("🚚 Order in transit:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: { ...prev.deliveryPhase, status: "IN_TRANSIT" },
        }));
      }
    });

    // Order delivered
    socket.on("order_delivered", (data) => {
      if (data.orderId === orderId) {
        console.log("✅ Order delivered:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: { ...prev.deliveryPhase, status: "DELIVERED" },
        }));
      }
    });

    // Preparation updates (images, notes)
    socket.on("preparation_update", (data) => {
      if (data.orderId === orderId) {
        console.log("📸 Preparation update received:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status || prev.status,
          tracking: data.tracking || prev.tracking,
          vendorPhase: data.vendorPhase || prev.vendorPhase,
          preparationUpdates: data.preparationUpdates || prev.preparationUpdates,
        }));
      }
    });

    return () => {
      socket.off("vendor_order_updated");
      socket.off("agent_accepted");
      socket.off("order_status_updated");
      socket.off("order_picked_up");
      socket.off("order_in_transit");
      socket.off("order_delivered");
      socket.off("preparation_update");
    };
  }, [orderId, fetchOrder]);

  // 🟣 PHASE 1: VENDOR ACTIONS
  const handleAccept = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "ACCEPT",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (err) {
      console.error("Failed to accept order:", err);
      //alert(error.response?.data?.message || "Failed to accept order");
      showSnackbar(err.response?.data?.message || "Failed to accept order", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStartPreparing = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "PREPARING",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (error) {
      console.error("Failed to start preparing order:", error);
      // alert(error.response?.data?.message || "Failed to start preparing order");
       showSnackbar(error.response?.data?.message || "Failed to start preparing order");
    } finally {
      setLoading(false);
    }
  };
  const handleReject = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "REJECT",
        reason: rejectionReason,
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
      setRejectDialog(false);
      setRejectionReason("");
    } catch (error) {
      console.error("Failed to reject order:", error);
      // alert(error.response?.data?.message || "Failed to reject order");
        showSnackbar(error.response?.data?.message || "Failed to reject order", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReadyForPickup = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "READY_FOR_PICKUP",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (error) {
      console.error("Failed to mark ready:", error);
      // alert(error.response?.data?.message || "Failed to mark items as ready");
      showSnackbar(error.response?.data?.message || "Failed to mark items as ready", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!order) return null;

  const vendorPhase = order.vendorPhase || {};
  const agentPhase = order.agentPhase || {};
  const deliveryPhase = order.deliveryPhase || {};

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4, backgroundColor: "#fff", p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <Button
          onClick={() => navigate("/vendor/orders")}
          sx={{ mb: 2, textTransform: "none", color: "#007185", fontSize: 14, p: 0 }}
        >
          ← Back to Orders
        </Button>

        <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: "#0f1111" }}>
          Order #{orderId.substring(0, 8).toUpperCase()}
        </Typography>
        <Typography variant="body2" color="#565959">
          Manage order preparation and delivery
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left: Order Items */}
        <Grid item xs={12} md={8}>
          {/* Items */}
          <Card sx={{ mb: 3, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#0f1111" }}>
                📦 Order Items
              </Typography>
              {order.items?.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                    mb: 1,
                    border: "1px solid #e7e7e7",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#0f1111">
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="#565959" sx={{ mt: 0.5 }}>
                      Qty: {item.qty}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} color="#b12704">
                    ₹{item.price}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e7e7e7" }}>
                <Typography variant="body2" fontWeight={700} color="#0f1111">
                  Total: <span style={{ color: "#b12704", fontSize: "1.1em" }}>₹{order.total}</span>
                </Typography>
                <Typography variant="caption" color="#565959">
                  Payment: {order.paymentMethod?.toUpperCase()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card sx={{ mb: 3, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#0f1111" }}>
                📍 Delivery Address
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: "#f9f9f9", border: "1px solid #e7e7e7", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, color: "#0f1111", fontWeight: 500 }}>
                  {order.address?.addressLine1}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: "#0f1111" }}>
                  {order.address?.city}, {order.address?.state} {order.address?.pincode}
                </Typography>
              </Paper>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card sx={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#0f1111" }}>
                📋 Order Timeline
              </Typography>
              <Stack spacing={1}>
                {order.tracking?.map((track, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 2,
                      backgroundColor: track.completed ? "#f0fdf4" : "#fafafa",
                      borderRadius: 1,
                      borderLeft: track.completed
                        ? "3px solid #10b981"
                        : "3px solid #d1d5db",
                      mb: 1,
                    }}
                  >
                    <CheckCircle
                      sx={{
                        fontSize: 22,
                        color: track.completed ? "#10b981" : "#d1d5db",
                        mr: 2,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="#0f1111">
                        {track.label}
                      </Typography>
                      <Typography variant="caption" color="#565959" sx={{ mt: 0.5 }}>
                        {new Date(track.time).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Vendor Phase Controls */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: "#0f1111" }}>
                🟣 Your Responsibility
              </Typography>

              {/* Vendor Phase Status */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1, border: "1px solid #e7e7e7" }}>
                <Typography variant="caption" color="#565959" fontWeight={600}>
                  Preparation Status
                </Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Chip
                    icon={<CheckCircle />}
                    label={(vendorPhase.status || "PENDING").replace(/_/g, " ")}
                    color={
                      vendorPhase.status === "ACCEPTED"
                        ? "success"
                        : vendorPhase.status === "REJECTED"
                        ? "error"
                        : "default"
                    }
                    sx={{ width: "100%", fontWeight: 600, fontSize: 12 }}
                  />
                </Box>
              </Box>

              {/* PHASE 1: PENDING - Show Accept/Reject */}
              {vendorPhase.status === "PENDING" && (
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleAccept}
                    disabled={loading}
                  >
                    ✓ Accept Order
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => setRejectDialog(true)}
                    disabled={loading}
                    startIcon={<Close />}
                  >
                    ✗ Reject Order
                  </Button>
                </Stack>
              )}

              {/* PHASE 1: ACCEPTED - Show Start Preparing */}
              {vendorPhase.status === "ACCEPTED" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ✓ Order Accepted
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Click below to start preparing the order
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, backgroundColor: "#f0f7ff", borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Estimated Ready Time
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                      <AccessTime sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }} />
                      {vendorPhase.estimatedReadyTime
                        ? new Date(vendorPhase.estimatedReadyTime).toLocaleTimeString()
                        : "30 mins"}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleStartPreparing}
                    disabled={loading}
                  >
                    ▶ Start Preparing
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => setRejectDialog(true)}
                    disabled={loading}
                    startIcon={<Close />}
                  >
                    ✗ Reject Order
                  </Button>
                </Stack>
              )}

              {/* PHASE 1: PREPARING - Can upload images & mark ready */}
              {vendorPhase.status === "PREPARING" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#fff3e0", borderRadius: 1, border: "1px solid #ff9800" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ⏳ Preparing Order
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      You can upload preparation images and notes. Click "Ready for Pickup" when done.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleReadyForPickup}
                    disabled={loading}
                  >
                    ✓ Ready for Pickup
                  </Button>
                </Stack>
              )}

              {/* PHASE 1: READY_FOR_PICKUP - Waiting for Agent */}
              {vendorPhase.status === "READY_FOR_PICKUP" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ✓ Items Ready for Pickup
                    </Typography>
                    {/* <Typography variant="caption" color="textSecondary">
                      Waiting for agent to arrive...
                    </Typography> */}
                  </Box>

                  {/* Show Agent Status */}
                  {agentPhase.status === "SEARCHING" && (
                    <Box sx={{ p: 2, backgroundColor: "#fff3e0", borderRadius: 1, border: "1px solid #ff9800" }}>
                      <Typography variant="caption" color="textSecondary">
                        🔍 Agent Status
                      </Typography>
                      <Chip
                        label="Agents Searching"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}

                  {agentPhase.status === "ACCEPTED" && (
                    <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        ✓ Agent Assigned
                      </Typography>
                      {/* <Chip
                        label="Agent Coming to Pickup"
                        size="small"
                        color="success"
                        sx={{ mt: 1 }}
                      /> */}
                    </Box>
                  )}

                  {agentPhase.status === "NONE" && (
                    <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                      <Typography variant="caption" color="error">
                        ⚠️ No agents available
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}

              {/* PHASE 1: REJECTED */}
              {vendorPhase.status === "REJECTED" && (
                <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    ✗ Order Rejected
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {vendorPhase.rejectionReason}
                  </Typography>
                </Box>
              )}

              {/* Divider */}
              <Box sx={{ my: 3, borderTop: "1px solid #e0e0e0" }} />

              {/* Show Agent & Delivery Status */}
              {agentPhase.status !== "NONE" && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: "block" }}>
                    🟠 Agent Phase
                  </Typography>
                  <Chip
                    label={agentPhase.status}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {deliveryPhase.status !== "NONE" && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: "block" }}>
                    🟡 Delivery Phase
                  </Typography>
                  <Chip
                    label={deliveryPhase.status}
                    size="small"
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Order</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for rejection"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this order"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={loading || !rejectionReason.trim()}
          >
            {loading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorOrderDetails;


